import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { EmptyState } from "./InventoryHome";
import { History, ArrowDownRight, ArrowUpRight, Eraser, RefreshCw, Plus } from "lucide-react";

const KIND_META = {
  initial_stock: { label: "Initial stock", color: "text-olive", bg: "bg-olive/10", Icon: Plus },
  adjust: { label: "Adjust", color: "text-slate-600", bg: "bg-slate-100", Icon: RefreshCw },
  write_off: { label: "Write-off", color: "text-red-600", bg: "bg-red-50", Icon: Eraser },
  transfer_in: { label: "Transfer in", color: "text-emerald-700", bg: "bg-emerald-50", Icon: ArrowDownRight },
  transfer_out: { label: "Transfer out", color: "text-amber-700", bg: "bg-amber-50", Icon: ArrowUpRight },
};

/** Movements tab — append-only ledger of every stock change. */
export default function MovementsTab({ sites, items, refreshTick }) {
  const [site, setSite] = useState("");
  const [itemId, setItemId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = { limit: 200 };
    if (site) params.site = site;
    if (itemId) params.item_id = itemId;
    api
      .get("/inventory/movements", { params })
      .then((r) => !cancel && setRows(r.data || []))
      .catch(() => !cancel && setRows([]))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [site, itemId, refreshTick]);

  const itemMap = useMemo(() => Object.fromEntries((items || []).map((i) => [i.item_id, i])), [items]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl">
      <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            data-testid="movements-site"
            value={site}
            onChange={(e) => setSite(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30"
          >
            <option value="">All sites</option>
            {sites.map((s) => (<option key={s} value={s}>{s}</option>))}
          </select>
          <select
            data-testid="movements-item"
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-olive/30 max-w-xs"
          >
            <option value="">All items</option>
            {items.map((i) => (
              <option key={i.item_id} value={i.item_id}>{i.name}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-slate-500">{loading ? "Loading…" : `${rows.length} entries`}</div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-slate-500">Loading movements…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          testid="movements-empty"
          icon={History}
          title="No movements yet"
          body="Every stock add, adjustment, transfer or write-off appears here as an immutable ledger entry."
        />
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="movements-list">
          {rows.map((m) => {
            const meta = KIND_META[m.kind] || { label: m.kind, color: "text-slate-600", bg: "bg-slate-100", Icon: History };
            const Icon = meta.Icon;
            const itemName = m.item_name || itemMap[m.item_id]?.name || m.item_id;
            const positive = (m.delta || 0) >= 0;
            return (
              <li
                key={m.movement_id}
                data-testid={`movement-row-${m.movement_id}`}
                className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50/60"
              >
                <div className={`w-9 h-9 rounded-xl ${meta.bg} grid place-items-center ${meta.color} shrink-0`}>
                  <Icon size={14} strokeWidth={1.9} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {itemName} <span className="text-slate-400 font-normal">·</span> <span className="text-slate-500">{m.site}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {meta.label}
                    {m.adjustment_type && (
                      <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-medium uppercase tracking-wider">
                        {m.adjustment_type.replace(/_/g, " ")}
                      </span>
                    )}
                    {m.reason ? ` · ${m.reason}` : ""}
                    {" · "}
                    {m.by_name || "—"}{" · "}
                    {new Date(m.at).toLocaleString()}
                  </div>
                </div>
                <div className={`text-sm font-semibold tabular-nums ${positive ? "text-olive" : "text-red-600"}`}>
                  {positive ? "+" : ""}{m.delta}
                </div>
                <div className="hidden sm:block text-[11px] text-slate-400 tabular-nums ml-3">
                  {m.before_qty} → {m.after_qty}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
